import { Button } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import OWButton from '../../../components/ow_button/OWButton';
import { cn } from '../../../utils/ClassNamesUtils';
import { useOscarTheme } from '../../context/OscarRoleContext';
import OscarDisclosure from '../shared/OscarDisclosure';
import { humanizeEnumValue, humanizeFieldLabel, humanizeToolName } from '../../utils/OscarLabels';

interface OscarApprovalCardProps {
	toolName: string;
	args: unknown;
	/** `destructiveHint` on the MCP tool — gets the louder treatment. */
	destructive?: boolean;
	/** The model's one-sentence reason for the write, from the harness. Display only. */
	rationale?: string;
	resolved?: boolean;
	approved?: boolean;
	/** Resolves false when the decision did not reach the harness, so the card unlocks. */
	onResolve?: (approved: boolean) => void | Promise<boolean>;
	/** A bulk decision covering this card is in flight — it must not take another. */
	locked?: boolean;
	className?: string;
}

/**
 * The write gate: `openwrench-internal-mcp` annotates every tool, reads auto-run
 * and writes pause here for the user. Destructive writes get a louder prompt.
 *
 * Every approval looks like an approval. A previous version rendered writes
 * whose payload contained a note through the chat message card, on the theory
 * that "here is some text, do you want it sent" reads better than a tool name
 * over a field list. It reads better and says the wrong thing: approving a work
 * order that happens to carry a note is not sending a message, and the button
 * promised "Send". That shell belongs to chat — suggested questions — and an
 * approval is not chat.
 *
 * NOT YET DESIGNED — there is no Figma node for approvals, which is a gap worth
 * closing since this is the feature's core safety surface. Built from the same
 * shell as the question cards so it is at least consistent.
 */
/**
 * Long enough that rendering it in full buries everything around it. A
 * generated CSV came through as one field and pushed the file's own name below
 * the fold, so the reader had to scroll a wall of rows to find out what they
 * were approving.
 */
const BULK_VALUE_CHARS = 140;

const isBulkValue = (value: unknown): boolean =>
	typeof value === 'string' && (value.length > BULK_VALUE_CHARS || value.indexOf('\n') !== -1);

/**
 * Fields whose value is prose a person will read — a note on a work order, a
 * comment to a vendor, a work order's description. These are what the write is
 * *about*, so they are always shown in full, however long: collapsing a note to
 * "Note — 3 lines · 240 characters" meant approving a message nobody had read,
 * which is exactly what the gate exists to prevent.
 *
 * Matched on the key's last word, so `note`, `noteText`, `internal_note` and
 * `vendorMessage` all qualify. File payloads (`content`, CSV text) do not, and
 * keep the collapsed treatment below.
 */
const MESSAGE_KEY = /(note|message|comment|reply|description)s?(text|body)?$|^(text|body)$/;

const isMessageField = (key: string, value: unknown): boolean =>
	typeof value === 'string' &&
	value.trim() !== '' &&
	MESSAGE_KEY.test(key.toLowerCase().replace(/[^a-z]/g, ''));

/** Exact UTF-8 bytes where the platform can give them, characters otherwise. */
const byteLength = (text: string): number =>
	typeof Blob === 'function' ? new Blob([text]).size : text.length;

/** "11 lines · 1.2 KB" — enough to judge the payload without reading it. */
const describeBulkValue = (text: string): string => {
	const bytes = byteLength(text);
	const size = bytes >= 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${bytes} characters`;
	const lines = text.split('\n').length;
	return lines > 1 ? `${lines} lines · ${size}` : size;
};

/**
 * What the call will actually write.
 *
 * A message — a note, a comment — comes first and in full, set off as a quote:
 * it is the thing being approved. Short fields — the name, the ids, the flags —
 * identify where it goes and are listed plainly after it. A bulk value is
 * collapsed to its shape ("11 lines · 1.2 KB") and opens on demand: the reader
 * almost never wants a file's whole contents to decide whether to save it, but
 * hiding it outright would mean approving something unseen.
 *
 * Anything nested falls back to JSON, which is rare and better shown than
 * hidden.
 */
const OscarApprovalArgs = ({ args }: { args: unknown }) => {
	const entries =
		args && typeof args === 'object' && !Array.isArray(args)
			? Object.entries(args as Record<string, unknown>)
			: null;
	const flat = entries && entries.every(([, value]) => typeof value !== 'object' || value === null);

	if (!entries || !flat) {
		return (
			<pre className="footnoteregular m-0 max-h-40 overflow-auto rounded bg-white p-xs text-character-secondary">
				{JSON.stringify(args, null, 2)}
			</pre>
		);
	}

	const messages = entries.filter(([label, value]) => isMessageField(label, value));
	const rest = entries.filter(([label, value]) => !isMessageField(label, value));
	const identifying = rest.filter(([, value]) => !isBulkValue(value));
	const bulk = rest.filter(([, value]) => isBulkValue(value));

	return (
		<div className="flex w-full min-w-0 flex-col gap-xs rounded bg-white p-xs">
			{messages.map(([label, value]) => (
				<div key={label} className="flex flex-col gap-2xs">
					<span className="footnoteregular text-character-secondary">
						{humanizeFieldLabel(label)}
					</span>
					{/* Scrolls rather than collapses: a very long note stays readable
					    without pushing the buttons out of the card. */}
					<div
						className="bodyregular max-h-60 overflow-y-auto whitespace-pre-wrap break-words border-0
							border-l-2 border-solid border-grey-4 pl-xs text-grey-9"
					>
						{String(value)}
					</div>
				</div>
			))}
			{identifying.length ? (
				<dl className="m-0 flex flex-col gap-2xs">
					{identifying.map(([label, value]) => (
						<div key={label} className="flex flex-col gap-0">
							<dt className="footnoteregular text-character-secondary">
								{humanizeFieldLabel(label)}
							</dt>
							<dd className="bodyregular m-0 whitespace-pre-wrap break-words text-grey-9">
								{humanizeEnumValue(String(value))}
							</dd>
						</div>
					))}
				</dl>
			) : null}
			{bulk.map(([label, value]) => (
				<OscarDisclosure
					key={label}
					label={`${humanizeFieldLabel(label)} — ${describeBulkValue(String(value))}`}
				>
					<pre className="footnoteregular m-0 max-h-40 overflow-auto whitespace-pre-wrap break-words text-character-secondary">
						{String(value)}
					</pre>
				</OscarDisclosure>
			))}
		</div>
	);
};

export const OscarApprovalCard = ({
	toolName,
	args,
	destructive,
	rationale,
	resolved,
	approved,
	onResolve,
	locked,
	className,
}: OscarApprovalCardProps) => {
	const theme = useOscarTheme();
	// Both controls stayed live until the stream confirmed the decision, so a
	// slow request or a delayed `approval_resolved` let the other one be clicked
	// too — and request arrival order decided whether the write happened. On the
	// feature's safety surface that is not acceptable.
	const [deciding, setDeciding] = useState(false);

	const decide = (approved: boolean) => {
		if (!onResolve || deciding || locked) {
			return;
		}
		setDeciding(true);
		// Unlocked only when the decision did not land. On success the stream
		// retires the card, and re-enabling would briefly offer a choice that has
		// already been made.
		Promise.resolve(onResolve(approved))
			.then((landed) => {
				if (landed === false) {
					setDeciding(false);
				}
			})
			.catch(() => setDeciding(false));
	};
	return (
		<div
			className={cn(
				'flex w-full flex-col gap-xs rounded p-xs',
				destructive ? 'border-2 border-solid border-character-danger bg-character-dangerBg' : null,
				destructive ? null : theme.surface,
				destructive ? null : theme.stroke,
				className
			)}
		>
			<span className="bodymedium text-grey-10">
				{destructive ? 'Confirm a destructive action' : 'Approval needed'}
			</span>
			<span className="bodyregular text-grey-9" title={toolName}>
				{`Oscar wants to run `}
				<span className="bodymedium">{humanizeToolName(toolName)}</span>
			</span>
			{/* The model's own reason, so the reader need not scroll back up the
			    conversation to work out why this write is being asked for. It is
			    what the model says, not a guarantee: the fields below are what runs. */}
			{rationale ? (
				<span className="bodyregular break-words text-grey-9">
					<span className="bodymedium">Why: </span>
					{rationale}
				</span>
			) : null}
			<OscarApprovalArgs args={args} />
			{resolved ? (
				<span className="footnoteregular text-character-secondary">
					{approved ? 'Approved — the agent ran it.' : 'Rejected — nothing ran.'}
				</span>
			) : (
				<div className="flex items-center justify-end gap-xs">
					<Button size="large" disabled={deciding || locked} onClick={() => decide(false)}>
						Reject
					</Button>
					<OWButton
						size="large"
						type="primary"
						danger={destructive}
						loading={deciding}
						disabled={locked}
						onClick={() => decide(true)}
					>
						Approve
					</OWButton>
				</div>
			)}
		</div>
	);
};

export default OscarApprovalCard;
